import {Send} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, IconButton, TextField, Typography} from '@mui/material';
import {FC, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {registerApi} from 'src/api/registration';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {BaseUserInfo} from 'src/types';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {getEvDriverNameFromBaseUser} from 'src/utils/userNameFormatter';
import theme from 'src/theme';

export const ResendRegistration: FC<{ row: BaseUserInfo }> = ({row}) => {
    // Re-sending an invitation is a write, and only the roles that may create the account in the
    // first place hold it.
    const canResend = useCan('registration.resendInvite');
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const [open, setOpen] = useState<boolean>(false);
    const [registrationLink, setRegistrationLink] = useState<string>('');
    const [linkNotFound, setLinkNotFound] = useState<boolean>(false);

    const dialogTitle = intl.formatMessage({id: 'resendRegistration'});

    const [triggerGetLink] = registerApi.useLazyGetRegistrationLinkQuery();
    const [triggerCompleteResend] = registerApi.useCompleteResendMutation();
    const [triggerConfirmResend] = registerApi.useConfirmResendMutation();

    const handleOpenModal = async () => {
        setOpen(true);
        setLinkNotFound(false);
        setRegistrationLink('');

        // Get registration link
        try {
            dispatch(preloaderChangeStatus(true));
            const response = await triggerGetLink({
                email: row.emailAddress,
                businessId: businessId ?? 0,
            }).unwrap();

            setRegistrationLink(response?.payload || '');
        } catch (error: any) {
            // Check if it's a 404 error
            if (error?.status === 404) {
                setLinkNotFound(true);
                // Don't show notification, don't close modal
            } else {
                // For other errors, show notification and close modal
                NotifyError(intl.formatMessage({id: 'errorGettingLink'}));
                setOpen(false);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleCloseModal = () => {
        setOpen(false);
        setRegistrationLink('');
        setLinkNotFound(false);
    };

    const handleResend = async () => {
        try {
            dispatch(preloaderChangeStatus(true));

            // Determine which endpoint to use based on the link
            const isConfirmRegistration = registrationLink.includes('confirmRegistration');

            if (isConfirmRegistration) {
                // User has password - use confirm/resend
                await triggerConfirmResend(row.emailAddress).unwrap();
            } else {
                // User doesn't have password - use complete/resend
                await triggerCompleteResend(row.emailAddress).unwrap();
            }

            NotifySuccess(intl.formatMessage({id: 'resendSuccess'}));
            handleCloseModal();
        } catch (error) {
            NotifyError(intl.formatMessage({id: 'resendError'}));
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(registrationLink);
        NotifySuccess(intl.formatMessage({id: 'linkCopied'}));
    };

    if (!canResend) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={{
                    width: '28px',
                    height: '28px',
                    p: '2px',
                    color: theme.palette.neutral[400],
                    backgroundColor: 'transparent',
                    '.MuiSvgIcon-root': {
                        fill: theme.palette.neutral[400],
                        width: '20px',
                    },
                }}
                onClick={handleOpenModal}
            >
                <Send/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
            >
                <Box sx={{padding: theme.spacing(3)}}>
                    <Typography variant="subtitle1" sx={{marginBottom: theme.spacing(2)}}>
                        <FormattedMessage id="registrationLinkForUser"
                                          values={{name: getEvDriverNameFromBaseUser(row)}}/>
                    </Typography>

                    {linkNotFound && (
                        <Box sx={{marginBottom: theme.spacing(3)}}>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.palette.error.main,
                                    textAlign: 'center',
                                    padding: theme.spacing(2),
                                    backgroundColor: theme.palette.error.light + '20',
                                    borderRadius: theme.spacing(1),
                                }}
                            >
                                <FormattedMessage id="registrationLinkNotFound"/>
                            </Typography>
                        </Box>
                    )}

                    {registrationLink && !linkNotFound && (
                        <Box sx={{marginBottom: theme.spacing(3)}}>
                            <TextField
                                fullWidth
                                value={registrationLink}
                                label={intl.formatMessage({id: 'registrationLink'})}
                                InputProps={{
                                    readOnly: true,
                                }}
                                multiline
                                rows={3}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleCopyLink}
                                sx={{marginTop: theme.spacing(1)}}
                            >
                                <FormattedMessage id="copyLink"/>
                            </Button>
                        </Box>
                    )}

                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
                        <Button variant="outlined" onClick={handleCloseModal}>
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleResend}
                            disabled={linkNotFound}
                        >
                            <FormattedMessage id="resend"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>
        </>
    );
};